import { ChangeEvent, FormEvent, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, Camera, Check, ImagePlus, Leaf, Loader2, Send, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import shareTipArtwork from "../../attached_assets/file_00000000ba5082118480e4007f04eba2_1788630543710.png";

const ShareTipsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("Nutrition");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState<"draft" | "published" | null>(null);
  const [error, setError] = useState("");

  const categories = [
    { value: "Nutrition", label: bn ? "পুষ্টি" : "Nutrition" },
    { value: "Lifestyle", label: bn ? "জীবনযাপন" : "Lifestyle" },
    { value: "Mental wellness", label: bn ? "মানসিক সুস্থতা" : "Mental wellness" },
    { value: "Fitness", label: bn ? "ফিটনেস" : "Fitness" },
    { value: "General", label: bn ? "সাধারণ" : "General" },
  ];

  const handleImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const saveTip = async (status: "draft" | "published") => {
    if (!user) return;
    setError("");

    if (!title.trim() || !body.trim()) {
      setError(bn ? "শিরোনাম এবং টিপ লিখুন" : "Add a title and tip before saving");
      return;
    }

    setSaving(status);
    let uploadedPath: string | null = null;

    try {
      let imageUrl: string | null = null;
      if (imageFile) {
        const safeFileName = imageFile.name.toLowerCase().replace(/[^a-z0-9.-]+/g, "-");
        uploadedPath = `${user.id}/${Date.now()}-${safeFileName}`;
        const { error: uploadError } = await supabase.storage
          .from("tip-images")
          .upload(uploadedPath, imageFile, { upsert: false });
        if (uploadError) throw uploadError;
        imageUrl = supabase.storage.from("tip-images").getPublicUrl(uploadedPath).data.publicUrl;
      }

      const { error: insertError } = await (supabase.from("health_tips" as any) as any)
        .insert({
          author_id: user.id,
          title: title.trim(),
          body: body.trim(),
          category,
          image_url: imageUrl,
          status,
        });

      if (insertError) throw insertError;

      toast.success(status === "published"
        ? (bn ? "টিপ শেয়ার হয়েছে" : "Tip shared")
        : (bn ? "ড্রাফট সংরক্ষিত হয়েছে" : "Draft saved"));
      navigate(-1);
    } catch (saveError: any) {
      if (uploadedPath) {
        await supabase.storage.from("tip-images").remove([uploadedPath]);
      }
      setError(saveError?.message || (bn ? "টিপ সংরক্ষণ করা যায়নি" : "Could not save the tip"));
    } finally {
      setSaving(null);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void saveTip("published");
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-10 text-[#203f3c]">
      <div className="mx-auto max-w-md px-5 pb-8 pt-5 sm:pt-7">
        <header className="relative overflow-hidden rounded-[28px] bg-white px-1 pb-4">
          <button type="button" onClick={() => navigate(-1)} aria-label={bn ? "ফিরে যান" : "Go back"} className="rounded-full p-1.5 text-[#174e49]">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="mt-3 pr-32">
            <h1 className="font-serif text-[25px] font-extrabold leading-tight text-[#174e49]">{bn ? "স্বাস্থ্য টিপ শেয়ার করুন" : "Share a health tip"}</h1>
            <p className="mt-2 text-sm leading-5 text-[#6d7b78]">{bn ? "আপনার রোগীদের আরও একটি ছোট পদক্ষেপ নিতে সাহায্য করুন।" : "Help your patients take one small step toward better health."}</p>
          </div>
          <img src={shareTipArtwork} alt="" aria-hidden="true" className="absolute right-0 top-0 h-[135px] w-[135px] object-contain" />
        </header>

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          <div>
            <label htmlFor="tip-title" className="mb-2 block text-xs font-extrabold text-[#273f3b]">{bn ? "টিপের শিরোনাম" : "Tip title"}</label>
            <input
              id="tip-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={bn ? "যেমন: সারাদিন পানি পান করুন" : "e.g. Stay hydrated throughout the day"}
              className="w-full rounded-2xl border border-[#dbe9e4] bg-white px-4 py-4 text-sm outline-none placeholder:text-[#8a9693] focus:border-[#4b9c90]"
            />
          </div>

          <div>
            <label htmlFor="tip-body" className="mb-2 block text-xs font-extrabold text-[#273f3b]">{bn ? "আপনার টিপ" : "Your tip"}</label>
            <div className="relative">
              <textarea
                id="tip-body"
                value={body}
                maxLength={250}
                onChange={(event) => setBody(event.target.value)}
                placeholder={bn ? "একটি ছোট, সহায়ক স্বাস্থ্য টিপ লিখুন..." : "Write a short, helpful health tip..."}
                rows={7}
                className="w-full resize-none rounded-2xl border border-[#dbe9e4] bg-white px-4 py-4 pb-8 text-sm leading-5 outline-none placeholder:text-[#8a9693] focus:border-[#4b9c90]"
              />
              <span className="absolute bottom-3 right-4 text-[10px] text-[#71807d]">{body.length} / 250</span>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-extrabold text-[#273f3b]">{bn ? "ক্যাটাগরি" : "Category"}</p>
            <div className="flex flex-wrap gap-2">
              {categories.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setCategory(item.value)}
                  className={`rounded-full border px-3 py-2 text-[10px] font-semibold ${category === item.value ? "border-[#168878] bg-[#e4f3ef] text-[#237e76]" : "border-[#e2ebe4] bg-white text-[#647470]"}`}
                >
                  {category === item.value && <Check className="mr-1 inline h-3 w-3" />}
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-extrabold text-[#273f3b]">{bn ? "ছবি (ঐচ্ছিক)" : "Image (optional)"}</p>
            {imagePreview ? (
              <div className="relative overflow-hidden rounded-2xl border border-[#dbe9e4] bg-[#eff8f2]">
                <img src={imagePreview} alt="" className="h-40 w-full object-cover" />
                <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} aria-label={bn ? "ছবি সরান" : "Remove image"} className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-[#b35f5f]">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-[#cfe3d8] bg-white p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#e4f3ef] text-[#237e76]">
                  <ImagePlus className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-bold text-[#20534f]">{bn ? "পোস্টে ছবি যোগ করুন" : "Add an image to your post"}</span>
                  <span className="mt-1 block text-[10px] text-[#71807d]">{bn ? "ছবি যোগ করলে কার্ডের ডান পাশে দেখা যাবে" : "It will appear on the right side of the tip card"}</span>
                </span>
                <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
              </label>
            )}
          </div>

          <div>
            <p className="mb-2 text-xs font-extrabold text-[#273f3b]">{bn ? "কার সাথে শেয়ার করবেন" : "Share with"}</p>
            <div className="flex items-center gap-3 rounded-2xl border border-[#dbe9e4] bg-white p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e4f3ef] text-[#237e76]"><Leaf className="h-5 w-5" /></span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-[#20534f]">{bn ? "সব সেবাকো রোগী" : "All Sebaco patients"}</span>
                <span className="mt-1 block text-[10px] text-[#71807d]">{bn ? "আপনার টিপ সকল রোগীর কাছে দৃশ্যমান হবে।" : "Your tip will be visible to all Sebaco patients."}</span>
              </span>
            </div>
          </div>

          {error && <p className="rounded-xl border border-[#efcaca] bg-[#fff5f5] px-3 py-2 text-xs font-medium text-[#b35f5f]">{error}</p>}

          <button type="submit" disabled={saving !== null} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#258e7f] py-4 text-sm font-extrabold text-white disabled:opacity-60">
            {saving === "published" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {saving === "published" ? (bn ? "শেয়ার হচ্ছে..." : "Sharing...") : (bn ? "টিপ শেয়ার করুন" : "Share tip")}
          </button>
          <button type="button" disabled={saving !== null} onClick={() => void saveTip("draft")} className="flex w-full items-center justify-center gap-2 py-1 text-sm font-bold text-[#237e76] disabled:opacity-60">
            {saving === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {saving === "draft" ? (bn ? "সংরক্ষণ হচ্ছে..." : "Saving...") : (bn ? "ড্রাফট হিসেবে সংরক্ষণ করুন" : "Save as draft")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShareTipsPage;